import { createContext, useContext, useState, useCallback, FC, ReactNode } from 'react'
import ApplicationData, { ApplicantData } from '../dtos/applicationData'
import { api } from '../services/apiClient'
import { calculatePercentages } from 'src/utils/applicationStepCalculator'
import { newApplicationNavigationRoutes, newApplicationJudgeNavigationRoutes } from 'src/constants/application'
import { useAuth } from './auth'
import { AxiosError } from 'axios'
import { ValidationError, useErrors } from './errors'
import { TbChevronsDownLeft } from 'react-icons/tb'

interface CreateApplicantReturn {
  isSuccess: boolean;
  title?: string;
  subtitle?: string;
}

interface ApplicationContextData {
  application: ApplicationData;
  updateApplication(appData: ApplicationData): void;
  saveApplication(appData: ApplicationData): Promise<string>;
  saveApplicationForLater(appData: ApplicationData): Promise<void>;
  createApplicant(applicant: ApplicantData): Promise<CreateApplicantReturn>;
  getApplication(): Promise<string>;
}

interface KickoutData {
  type: string,
  redirect?: string;
  message?: string,
}
interface KickoutResponse {
  data: KickoutData
};

const ApplicationContext = createContext<ApplicationContextData>({} as ApplicationContextData)

export const ApplicationProvider: FC<{ children: ReactNode }> = ({children}) => {
  const [application, setApplication] = useState({} as ApplicationData)
  const { validateCookies } = useAuth()
  const { triggerToast, clearToast } = useErrors()

  const prepareApplicationToSave = (appData: ApplicationData): ApplicationData => {
    const routes = appData?.insuranceType === 'LAWYER' ? newApplicationNavigationRoutes : newApplicationJudgeNavigationRoutes

    const {
      completionPercentage,
      subSectionPercentage
    } = calculatePercentages(appData.stoppedAt, routes, false, appData?.insuranceType === 'JUDGE')

    return {
        ...appData,
        completionPercentage: completionPercentage,
        subSectionPercentage: String(subSectionPercentage)
      }
  }

  const updateApplication = useCallback((appData: ApplicationData)=> {
    setApplication(appData)
  }, [setApplication])

  const saveApplication = useCallback(async (appData: ApplicationData) : Promise<string>  => {
    clearToast()
    try {
      const { applicant, ...rest } = prepareApplicationToSave(appData)
      let result = await api.patch('/Applications', {
        step: 1,
        data: {
          ...rest
        }
      })
      updateApplication(appData)
      return result.data;
    } catch(error: any | AxiosError) {
      // Log actual error details to browser console
      if(error?.response?.data?.errorDetails) {
        console.error(error.response.data.errorDetails)
      }

      // Duplicates renewal.tsx
      if (error?.response?.status === 400) {
        const errors = error?.response?.data as ValidationError[]
        triggerToast(errors)
      }
      throw new Error('Error trying to save data. Please, try again')
    }
  }, [updateApplication, triggerToast, clearToast])

  const saveApplicationForLater = useCallback(async (appData: ApplicationData) => {
    clearToast()
    try {
      const { applicant, ...rest } = prepareApplicationToSave(appData)
      await api.post('/Applications/SaveForLater', {
        step: 1,
        data: {
          ...rest
        }
      })
      updateApplication(appData)
    } catch(error: any | AxiosError) {
      // Log actual error details to browser console
      if(error?.response?.data?.errorDetails) {
        console.error(error.response.data.errorDetails)
      }

      // Duplicates renewal.tsx
      if (error?.response?.status === 400) {
        const errors = error?.response?.data as ValidationError[]
        triggerToast(errors)
      }
      throw new Error('Error trying to save and send magic link. Please, try again')
    }
  }, [updateApplication, triggerToast, clearToast])

  const getApplication = useCallback(async () => {
    try {
      const { data } = await api.get<{ id: number, step: string, data: ApplicationData }>('/Applications')

      setApplication(prev => ({ ...prev, ...data.data, id: data.id }))
      
      if(data.data.aopRedirectRoute != null){
        return data.data.aopRedirectRoute;
      }
     
      return data.data.stoppedAt ?? '/new'
    } catch(error) {
      return '/new'
    }
  }, [])

  const createApplicant = useCallback(async (applicant: ApplicantData) => {
    if(!applicant.emailAddress) throw new Error('No email has been sent')
    try{
      const {
        firstName,
        lastName,
        emailAddress,
        isFirmAdministrator
      } = applicant

      const { data } = await api.post('/Applicants', {
        firstName,
        lastName,
        email: emailAddress,
        isFirmAdministrator
      })

      const cookiesTested = await validateCookies()


      //RESPOND_REGISTERED has to do with TLIE-401
      const RESPOND_REGISTERED = {
        isSuccess: false,
        title: 'Registered customer',
        subtitle: 'It looks like you are already a registered customer. Please check your inbox as we have sent you a link to access your firm’s customer portal.'
      };

      const RESPOND_ADMIN = {
        isSuccess: false,
        title: 'Logged in as Admin',
        subtitle: 'You were logged in as an Administrator. We have logged you out. Please click Save & Continue again to proceed as an Applicant.'
      };

      const RESPOND_IN_PROGRESS = {
        isSuccess: false,
        title: 'Application in progress',
        subtitle: 'This e-mail is already being used with an application currently in progress. We have sent you an e-mail with a link for you to continue where you left off.'
      };
      const RESPOND_COMPLETED_OR_SUBMITTED = {
        isSuccess: false,
        title: 'Application completed',
        subtitle: 'Thank you for submitting an application with this e-mail. We are currently reviewing your application, and will be in touch with you shortly.'
      };

      if(!cookiesTested) {
        const { applicationStatus, isCustomer } = data
        if(isCustomer === true) {
          await api.post('/MagicLinks', { email: emailAddress })
          return RESPOND_REGISTERED;
        } else {
          if (applicationStatus === 0) {
            await api.post('/MagicLinks', { email: emailAddress })
            return RESPOND_IN_PROGRESS;
          }
          return RESPOND_COMPLETED_OR_SUBMITTED;
        }
      } else {

        //kickout checks
        var appData = {
          ...application,
          applicant: {...applicant, applicantId: data.userId},
          firmSection: { firmName: '' }
        };
        const preparedData = prepareApplicationToSave(appData);
        const kickoutResponse: KickoutResponse = await api.patch('/Applications/CheckForKickout',{
          data: {
            ...preparedData
          }
        });

        //See TLIE-573 575 576
        if (kickoutResponse.data.type !== "OK") {
          if (kickoutResponse.data.type === "KICKOUT_ADMIN") {
            return {
              ...RESPOND_ADMIN,
            };
          }

          if (kickoutResponse.data.type === "KICKOUT_CUSTOMER") {
            await api.post('/MagicLinks', { email: cookiesTested.email })
            return {
              ...RESPOND_REGISTERED
            };
          }

          if (kickoutResponse.data.type === "KICKOUT_SUBMITTED") {
            return {
              ...RESPOND_COMPLETED_OR_SUBMITTED,
              title: "Application submitted"
            };
          }
          if (kickoutResponse.data.type === "KICKOUT_COMPLETED") {
            return {
              ...RESPOND_COMPLETED_OR_SUBMITTED,
              title: "Application completed"
            }
          }
          if (kickoutResponse.data.type === "KICKOUT_IN_PROGRESS") {
            //redirect to where they stopped at in the other In Progress application
            await api.post('/MagicLinks', { email: emailAddress, redirect: kickoutResponse.data.redirect });
            return RESPOND_IN_PROGRESS;
            // TODO: see if it's acceptable to just "drop them back in" to the application.
            // Redirect the user to kickoutResponse.data.redirect straight from here,
            // skip the modal message and MagicLink email redirect steps.
          }
        }

        await saveApplication({
          ...application,
          applicant: {...applicant, applicantId: data.userId},
          firmSection: { firmName: '' }
        })

        await getApplication()
      }
      return { isSuccess: true }
    } catch(_) {
      return { isSuccess: false, title: 'Unexpected Error', subtitle: 'Please, try again later.' }
    }
  }, [validateCookies, saveApplication, application, getApplication])

  return (
    <ApplicationContext.Provider
      value={{
        application,
        updateApplication,
        saveApplication,
        createApplicant,
        saveApplicationForLater,
        getApplication
      }}
    >
      {children}
    </ApplicationContext.Provider>
  )
}

export function useApplication() {
  const context = useContext(ApplicationContext)

  if(!context) throw new Error('useApplication must be used within an AuthProvider!')

  return context
}